import Auth from '@aws-amplify/auth'
import { Box, IconButton, Tooltip } from '@material-ui/core'
import Avatar from '@material-ui/core/Avatar'
import { Theme, withStyles } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import CloseIcon from '@material-ui/icons/Close'
import { useLocation } from '@reach/router'
import { Link, navigate } from 'gatsby'
import { TFunction } from 'i18next'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  sendExtensionMessage,
  sendToExtension,
} from '../../../utils/extensionHelper'
import { useAppState } from '../../appState'
import {
  BREAKPOINTS,
  EXTENSION_ID,
  FONTS,
  ROUTES,
  ShoppingBoardView,
} from '../../constants'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import { isUserAuthenticated } from './../../../utils/isUserAuthenticated'
import birdAvatar from './../../assets/images/bird-avatar.png'
import burgerMenuIcon from './../../assets/images/burger-menu.svg'
import chirpyestBirdIcon from './../../assets/images/chirpyest.svg'
import chirpyestTextLogo from './../../assets/images/chirpyest-text-logo.png'
import searchIcon from './../../assets/images/search.svg'
import ContentContainer from './../contentContainer'
import SignInModal from './../signInModal'
import DropdownMenu from './DropdownMenu'
import HeaderPopover from './HeaderPopover'
import MobileMenu from './MobileMenu'
import {
  getLoggedInMenuExtras,
  getLoggedInMobileNav,
  getLoggedInToolsItems,
  getLoggedOutDesktopLinks,
  getLoggedOutMenuExtras,
  getLoggedOutMobileNav,
  NavItem,
} from './navConfig'
import { styles } from './styles'

const CullectorTooltip = withStyles((theme: Theme) => ({
  arrow: {
    color: theme.palette.primary.main,
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontFamily: FONTS.Graphik.GraphikMedium,
    fontSize: '0.75rem',
    letterSpacing: '0.02em',
    padding: '10px 14px',
    borderRadius: 6,
    maxWidth: 'none',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.18)',
  },
}))(Tooltip)

interface CHRHeaderProps {
  t: TFunction
  openSignInModal: boolean
  isJoinPage: boolean
  isScrollable?: boolean
  withBanner?: boolean
}

const MOBILE_MENU_ID = 'chr-header-mobile-menu'
const HEADER_SCROLL_SHRINK_ENTER_THRESHOLD = 40
const HEADER_SCROLL_SHRINK_EXIT_THRESHOLD = 4

const CHRHeader = ({
  t,
  openSignInModal: openSignInModalProp,
  isJoinPage = false,
}: CHRHeaderProps) => {
  const classes = styles()
  const [appState, dispatch] = useAppState()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isSignInModal, setIsSignInModal] = useState(false)
  const [shopOpen, setShopOpen] = useState(false)
  const [isDropDownMenuOpen, setDropDownMenuStatus] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  const isLoggedIn = appState.userId > 0
  // Static HTML is always logged-out, so until SessionCheck settles the session (silent
  // refresh may be in flight) keep the auth controls laid out but invisible instead of
  // flashing a signed-out header and then swapping it.
  const isAuthPending = !appState.authResolved
  const { pathname } = useLocation()
  const isDesktop = useMediaQuery(`@media (min-width: ${BREAKPOINTS.lg}px)`)

  const headerContentRef = useRef<HTMLDivElement>(null)

  let timeout = null

  const urlParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams()
  const signOutFromApp = urlParams.get('signOutFromApp')

  useOnClickOutside(headerContentRef, () => setShopOpen(false), shopOpen)

  const handleSignOut = useCallback(async () => {
    dispatch({ type: 'SIGN_OUT' })
    navigate(ROUTES.home)
    window.localStorage.removeItem('user_id')
    window.localStorage.removeItem('movedItems')
    window.localStorage.removeItem('ig_token')
    window.localStorage.removeItem('referralId')
    window.localStorage.removeItem('profile')
    sendExtensionMessage({ action: 'signOut' })
    sendToExtension('CHR_SIGN_OUT')
    if (window.chrome?.runtime?.sendMessage) {
      window.chrome.runtime.sendMessage(
        EXTENSION_ID,
        { action: 'signOut' },
        (_response: any) => {}
      )
    }

    Auth.signOut()
  }, [dispatch])

  useEffect(() => {
    isUserAuthenticated().then(res => {
      openSignInModalProp && !res && setIsSignInModal(true)
    })
  }, [openSignInModalProp])

  useEffect(() => {
    if (signOutFromApp) {
      handleSignOut()
    }
  }, [signOutFromApp, handleSignOut])

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname])

  useEffect(() => {
    if (isDesktop) {
      setMobileMenuOpen(false)
    }
  }, [isDesktop])

  useEffect(() => {
    let scrollRafId: number | null = null

    const evaluateScroll = () => {
      scrollRafId = null
      setIsScrolled(prevIsScrolled => {
        const threshold = prevIsScrolled
          ? HEADER_SCROLL_SHRINK_EXIT_THRESHOLD
          : HEADER_SCROLL_SHRINK_ENTER_THRESHOLD
        return window.scrollY > threshold
      })
    }

    const handleScroll = () => {
      if (scrollRafId === null) {
        scrollRafId = window.requestAnimationFrame(evaluateScroll)
      }
    }

    evaluateScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (scrollRafId !== null) {
        window.cancelAnimationFrame(scrollRafId)
      }
    }
  }, [])

  const openSignInModal = useCallback(() => {
    setIsSignInModal(true)
  }, [])

  const closeSignInModal = useCallback(() => {
    setIsSignInModal(false)
  }, [])

  const handleReferFriend = useCallback(async () => {
    const username = appState.username
    const userId = appState.userId
    if (!username || !userId) return
    const baseUrl =
      process.env.GATSBY_SITE_URL ||
      (typeof window !== 'undefined' ? window.location.origin : '')
    const joinLink = `${baseUrl}/join/${username}-${userId}`
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(joinLink)
      }
      toast.success(String(t('messages.copiedToClipboard')))
    } catch {
      toast.error(String(t('messages.somethingWentWrong')))
    }
  }, [appState.username, appState.userId, t])

  const shoppingBoardLink = useMemo(() => {
    if (appState.username) {
      let link = ROUTES.mainBoard.replace(':userName', appState.username)

      const defaultView =
        appState?.userProfile?.defaultShoppingBoardView ||
        ShoppingBoardView.ALL_ITEMS

      if (defaultView === ShoppingBoardView.ALL_FOLDERS) {
        link += '#folders'
      }

      return link
    }
    return ROUTES.settings
  }, [appState.username, appState.userProfile])

  const showCloseIcon = pathname === '/search' && !isDesktop

  // Shared by every plain desktop nav link so `whiteText` (join-page mode)
  // is applied consistently — build it once instead of repeating the ternary.
  const navLinkClass = `${classes.headerInlineLink} ${
    isJoinPage ? classes.whiteText : ''
  }`

  const loggedOutDesktopLinks = useMemo(() => getLoggedOutDesktopLinks(t), [t])
  const loggedOutMenuExtras = useMemo(() => getLoggedOutMenuExtras(t), [t])
  const loggedOutMobileNav = useMemo(() => getLoggedOutMobileNav(t), [t])
  const loggedInToolsItems = useMemo(
    () => getLoggedInToolsItems(t, shoppingBoardLink),
    [t, shoppingBoardLink]
  )
  const loggedInMenuExtras = useMemo(() => getLoggedInMenuExtras(t), [t])
  const loggedInMobileNav = useMemo(
    () => getLoggedInMobileNav(t, shoppingBoardLink),
    [t, shoppingBoardLink]
  )

  const mobileNavItems: NavItem[] = isLoggedIn
    ? loggedInMobileNav
    : loggedOutMobileNav

  const renderDesktopNavLink = (item: NavItem) => (
    <Link
      key={item.key}
      rel="preconnect"
      to={item.to || '/'}
      className={navLinkClass}
    >
      {item.label}
    </Link>
  )

  const searchControl = (
    <IconButton
      style={{ padding: 0 }}
      className={`${classes.headerMobileIconBtn} ${
        isJoinPage ? classes.searchOnColoredBg : classes.searchButton
      }`}
      aria-label={String(t('header.search'))}
      onClick={() => {
        if (showCloseIcon) {
          navigate(-1)
        } else {
          navigate(ROUTES.search)
        }
      }}
    >
      {showCloseIcon ? (
        <CloseIcon color="primary" />
      ) : (
        <img
          src={searchIcon}
          alt=""
          height={16}
          width={21}
          className={isJoinPage ? classes.whiteLogo : ''}
        />
      )}
    </IconButton>
  )

  return (
    <header
      className={`${classes.headerShell} ${
        isScrolled ? classes.headerShellScrolled : ''
      } ${isJoinPage ? classes.coloredHeader : ''}`}
    >
      <ContentContainer customStyle="">
        <div ref={headerContentRef} className={classes.headerNavInner}>
          <nav
            className={`${classes.navigation} ${
              isDesktop ? classes.navigationFull : ''
            }`}
          >
            <Link
              rel="preconnect"
              to={isLoggedIn ? ROUTES.member : ROUTES.home}
              className={classes.headerLogoLink}
              aria-label={String(t('shared.chirpyest'))}
            >
              <img
                src={chirpyestBirdIcon}
                alt={String(t('shared.chirpyest'))}
                className={`${classes.headerBirdIcon} ${
                  isScrolled ? classes.headerBirdIconScrolled : ''
                } ${isJoinPage ? classes.whiteLogo : ''}`}
              />
              <img
                src={chirpyestTextLogo}
                alt={String(t('shared.chirpyest'))}
                className={`${classes.headerLogoImg} ${
                  isScrolled ? classes.headerLogoImgScrolled : ''
                } ${isJoinPage ? classes.whiteLogo : ''}`}
              />
            </Link>
            <div className={classes.desktopLinks}>
              <button
                type="button"
                className={`${classes.headerShopTrigger} ${classes.desktopLinks}`}
              >
                <span
                  onClick={() => setDropDownMenuStatus(s => !s)}
                  onMouseOver={() => {
                    clearTimeout(timeout)
                    if (!isDropDownMenuOpen) {
                      setDropDownMenuStatus(true)
                    }
                  }}
                  onMouseLeave={() => {
                    timeout = setTimeout(() => {
                      setDropDownMenuStatus(false)
                    }, 1000)
                  }}
                  style={{
                    textDecoration: isDropDownMenuOpen ? 'underline' : 'none',
                    color: isJoinPage ? 'white' : 'black',
                  }}
                >
                  {t('shared.shop')}
                </span>
              </button>

              <Link
                rel="preconnect"
                to={ROUTES.brands}
                className={navLinkClass}
              >
                {String(t('shared.brands'))}
              </Link>

              <Link
                rel="preconnect"
                to={ROUTES.theChirpyest}
                className={navLinkClass}
              >
                {String(t('header.community'))}
              </Link>
              <Link rel="preconnect" to={ROUTES.blog} className={navLinkClass}>
                {String(t('header.blog'))}
              </Link>
              <CullectorTooltip
                title={String(t('header.cullectorTooltip'))}
                arrow
                placement="bottom"
                enterDelay={200}
              >
                <Link
                  rel="preconnect"
                  to={ROUTES.cullector}
                  className={navLinkClass}
                >
                  {String(t('header.cullector'))}
                </Link>
              </CullectorTooltip>
            </div>

            {/* —— Desktop —— */}
            <div
              className={classes.headerActionsDesktop}
              style={isAuthPending ? { visibility: 'hidden' } : undefined}
            >
              {!isLoggedIn && (
                <>
                  {loggedOutDesktopLinks.map(renderDesktopNavLink)}
                  <button
                    type="button"
                    className={`${classes.headerSignInCta} ${
                      isJoinPage ? classes.whiteText : ''
                    }`}
                    onClick={openSignInModal}
                  >
                    {String(t('header.signIn'))}
                  </button>
                  <HeaderPopover
                    menuLabel={String(t('header.menu'))}
                    align="right"
                    items={loggedOutMenuExtras}
                    onSignIn={openSignInModal}
                    trigger={
                      <img
                        src={burgerMenuIcon}
                        alt={String(t('header.menu'))}
                        className={`${classes.menuIcon} ${
                          isJoinPage ? classes.whiteLogo : ''
                        }`}
                      />
                    }
                  />
                </>
              )}

              {isLoggedIn && (
                <>
                  <Link
                    rel="preconnect"
                    to={shoppingBoardLink}
                    className={navLinkClass}
                  >
                    {String(t('header.yourShoppingBoard'))}
                  </Link>

                  <Link
                    rel="preconnect"
                    to={ROUTES.createShareLink}
                    className={navLinkClass}
                  >
                    {String(t('createLink.createShareLink'))}
                  </Link>

                  {searchControl}

                  <HeaderPopover
                    menuLabel={String(t('header.yourAccount'))}
                    align="right"
                    items={loggedInMenuExtras}
                    onSignOut={handleSignOut}
                    onReferFriend={handleReferFriend}
                    trigger={
                      <Avatar className={classes.headerAvatar}>
                        <img
                          src={appState?.userProfile?.imageUrl || birdAvatar}
                          alt=""
                          className={classes.logo}
                          onError={() => {
                            dispatch({
                              type: 'UPDATE_USER_DATA',
                              userProfile: {
                                ...appState.userProfile,
                                imageUrl: birdAvatar,
                              },
                            })
                          }}
                        />
                      </Avatar>
                    }
                  />
                </>
              )}
            </div>

            {/* —— Mobile —— */}
            <div className={classes.headerActionsMobile}>
              {isLoggedIn && searchControl}
              <button
                type="button"
                className={`${classes.menuButton} ${classes.menuContainer}`}
                aria-expanded={mobileMenuOpen}
                aria-controls={MOBILE_MENU_ID}
                aria-label={String(t('header.menu'))}
                onClick={() => setMobileMenuOpen(true)}
              >
                <img
                  src={burgerMenuIcon}
                  alt={String(t('header.menu'))}
                  className={`${classes.menuIcon} ${
                    isJoinPage ? classes.whiteLogo : ''
                  }`}
                />
              </button>
            </div>
          </nav>
        </div>
      </ContentContainer>
      {isDesktop && (
        <Box
          onMouseEnter={() => clearTimeout(timeout)}
          onMouseLeave={() => {
            timeout = setTimeout(() => {
              setDropDownMenuStatus(false)
            }, 200)
          }}
        >
          <DropdownMenu
            openStatus={isDropDownMenuOpen}
            handleClose={() => setDropDownMenuStatus(false)}
          />
        </Box>
      )}

      {!isDesktop && (
        <MobileMenu
          id={MOBILE_MENU_ID}
          open={mobileMenuOpen}
          isLoggedIn={isLoggedIn}
          navItems={mobileNavItems}
          onClose={() => setMobileMenuOpen(false)}
          onSignIn={openSignInModal}
          onSignOut={handleSignOut}
          onReferFriend={handleReferFriend}
          isReferralBonus={appState?.userProfile?.isReferralBonus}
        />
      )}

      <SignInModal handleClose={closeSignInModal} openStatus={isSignInModal} />
    </header>
  )
}

export default withTranslation()(CHRHeader)
